import { config } from './config';

interface Payload {
  input: string;
  voice_id: string;
  model: string;
  language: string;
  prompt?: string;
}

interface TextNode {
  text: string;
  type: 'text';
}

interface EmotionNode {
  type: 'emotion';
  attrs: {
    emotion: string;
    disabled: boolean;
  };
}

interface ParagraphNode {
  content: Array<TextNode | EmotionNode>;
  type: 'paragraph';
}

interface DocNode {
  content: ParagraphNode[];
  type: 'doc';
}

type EditorContent = DocNode;

const audioMIMEType = 'audio/mpeg';

/**
 * Checks if MediaSource API is supported for audio/mpeg
 * @returns true if MediaSource is available and supports audio/mpeg
 */
function isMediaSourceSupported(): boolean {
  return (
    typeof MediaSource !== 'undefined' &&
    // eslint-disable-next-line compat/compat
    MediaSource.isTypeSupported(audioMIMEType)
  );
}

/**
 * Convert SSML to plain text, extracting only text content
 * Example: <speechify:emotion emotion="bright">text</speechify:emotion> -> text
 */
function ssmlToText(ssml: string): string {
  let text = ssml.replace(
    /<speechify:emotion\s+emotion="[^"]+">([^<]*)<\/speechify:emotion>/g,
    '$1',
  );
  
  // Remove all remaining XML tags (including <speak>)
  text = text.replace(/<[^>]+>/g, '');
  
  return text.trim();
}

/**
 * Parse SSML or bracketed text format into EditorContent structure
 * 
 * Input formats supported:
 * - SSML: <speak><speechify:emotion emotion="bright">text</speechify:emotion></speak>
 *   → Extracts plain text only (emotions are stripped)
 * - Bracketed: "text [emotion] more text"
 *   → Converts brackets to emotion nodes with highlighting
 */
export function parseTextToEditorContent(input: string): EditorContent[] {
  const text = input.includes('<') ? ssmlToText(input) : input;
  
  const lines = text.split('\n');
  const paragraphs: ParagraphNode[] = [];

  for (const line of lines) {
    if (line.trim() === '') {
      paragraphs.push({
        content: [],
        type: 'paragraph',
      } as ParagraphNode);
      continue;
    }

    const content: Array<TextNode | EmotionNode> = [];
    
    // Match patterns like [emotion], [chuckle], [sigh], etc.
    const emotionRegex = /\[([^\]]+)\]/g;
    let lastIndex = 0;
    let match;

    while ((match = emotionRegex.exec(line)) !== null) {
      // Add text before the emotion tag
      if (match.index > lastIndex) {
        const textBefore = line.substring(lastIndex, match.index);
        if (textBefore) {
          content.push({
            text: textBefore,
            type: 'text',
          } as TextNode);
        }
      }

      // Add emotion node
      const emotion = match[1].toLowerCase();
      content.push({
        type: 'emotion',
        attrs: {
          emotion,
          disabled: false,
        },
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last emotion tag
    if (lastIndex < line.length) {
      const remainingText = line.substring(lastIndex);
      if (remainingText) {
        content.push({
          text: remainingText,
          type: 'text',
        } as TextNode);
      }
    }

    // Only add paragraph if it has content
    if (content.length > 0) {
      paragraphs.push({
        content,
        type: 'paragraph',
      } as ParagraphNode);
    }
  }

  // If no paragraphs were created, create one with the original text
  if (paragraphs.length === 0) {
    paragraphs.push({
      content: [
        {
          text: text,
          type: 'text',
        } as TextNode,
      ],
      type: 'paragraph',
    } as ParagraphNode);
  }

  return [
    {
      content: paragraphs,
      type: 'doc',
    } as DocNode,
  ];
}

export type AudioSource = 
  | { type: 'url'; playbackUrl: string; downloadUrl: string }
  | { type: 'stream'; createStream: () => Promise<ReadableStream<Uint8Array>> };

/**
 * Converts a Buffer to a browser-compatible Uint8Array with proper ArrayBuffer
 */
function bufferToUint8Array(buffer: Buffer): Uint8Array {
  const arrayBuffer = new ArrayBuffer(buffer.byteLength);
  const uint8Array = new Uint8Array(arrayBuffer);
  uint8Array.set(buffer);
  return uint8Array;
}

/**
 * Creates a Blob URL from Uint8Array chunks
 */
function createBlobUrl(chunks: Uint8Array[]): string {
  const blob = new Blob(chunks as BlobPart[], { type: audioMIMEType });
  return URL.createObjectURL(blob);
}

async function readUint8ArrayStreamToChunks(
  stream: ReadableStream<Uint8Array>,
): Promise<Uint8Array[]> {
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
      }
    }
  } catch (error) {
    console.error('Error reading audio stream:', error);
    throw error;
  } finally {
    reader.releaseLock();
  }
  return chunks;
}

/**
 * Sets up MediaSource for progressive streaming playback
 * Appends chunks as they arrive from the stream
 * Falls back to blob URL if MediaSource is not supported
 * @param stream - ReadableStream of audio chunks
 * @param onPlaybackReady - Callback when enough data is buffered to start playback
 * @returns Promise that resolves with playback URL when MediaSource is ready
 */
export function setupProgressiveMediaSource(
  stream: ReadableStream<Uint8Array>,
  onPlaybackReady?: (audioUrl: string) => void,
): Promise<string> {
  // Check MediaSource support - fallback to blob URL if not supported
  if (!isMediaSourceSupported()) {
    return readUint8ArrayStreamToChunks(stream).then((chunks) => {
      const blobUrl = createBlobUrl(chunks);  
      onPlaybackReady?.(blobUrl);
      return blobUrl;
    });
  }

  return new Promise((resolve, reject) => {
    // Disabling since non supported in iOS Safari 11.0-11.2 is handled by the blob URL fallback
    // eslint-disable-next-line compat/compat
    const mediaSource = new MediaSource();
    const playbackUrl = URL.createObjectURL(mediaSource);
    let sourceBuffer: SourceBuffer | null = null;
    let hasStartedPlaying = false;
    let chunkCount = 0;
    let sourceOpenFired = false;
    const INITIAL_BUFFER_SECONDS = 0.1; // Start playing after 0.1s buffered

    mediaSource.addEventListener('error', () => {
      if (!sourceOpenFired) {
        reject(new Error('MediaSource failed to initialize'));
      }
    });

    resolve(playbackUrl);

    mediaSource.addEventListener('sourceopen', async () => {
      sourceOpenFired = true;
      try {
        sourceBuffer = mediaSource.addSourceBuffer(audioMIMEType);
        
        const reader = stream.getReader();
        let isAppending = false;
        const chunkQueue: Uint8Array[] = [];

        const processChunkQueue = () => {
          if (isAppending || !sourceBuffer || chunkQueue.length === 0) {
            return;
          }

          if (sourceBuffer.updating) {
            return;
          }

          const chunk = chunkQueue.shift();
          if (!chunk) return;

          isAppending = true;
          
          const arrayBuffer = chunk.buffer instanceof ArrayBuffer
            ? chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength)
            : new Uint8Array(chunk).buffer;

          sourceBuffer.appendBuffer(arrayBuffer);
          chunkCount++;

          if (!hasStartedPlaying) {
            const shouldStart = chunkCount === 1 || 
              (sourceBuffer.buffered.length > 0 && 
                typeof sourceBuffer.buffered.end(0) === 'number' && 
                !isNaN(sourceBuffer.buffered.end(0)) && 
                sourceBuffer.buffered.end(0) >= INITIAL_BUFFER_SECONDS);
            
            if (shouldStart) {
              hasStartedPlaying = true;
              onPlaybackReady?.(playbackUrl);
            }
          }
        };

        sourceBuffer.addEventListener('updateend', () => {
          isAppending = false;
          void processChunkQueue();
        });

        sourceBuffer.addEventListener('error', (e) => {
          console.error('SourceBuffer error:', e);
        });

        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              const waitForQueue = setInterval(() => {
                if (chunkQueue.length === 0 && !isAppending) {
                  clearInterval(waitForQueue);
                  try {
                    if (mediaSource.readyState === 'open') {
                      mediaSource.endOfStream();
                    }
                  } catch (error) {
                    console.error('Error ending stream:', error);
                  }
                }
              }, 100);
              break;
            }

            if (!value) continue;

            chunkQueue.push(value);
            processChunkQueue();
          }

          reader.releaseLock();
        } catch (error) {
          reader.releaseLock();
          console.error('Error reading stream:', error);
        }
      } catch (error) {
        reject(error instanceof Error ? error : new Error('Failed to setup MediaSource'));
      }
    });
  });
}

const requestTtsAudioStream = async (
  payload: Payload,
): Promise<ReadableStream<Uint8Array>> => {
  const ttsBackendUrl = config.demoBackendUrl;
  const plainText = ssmlToText(payload.input);

  const streamPayload = {
    text: plainText,
    voiceId: payload.voice_id,
    prompt: payload.prompt,
  };
  const response = await fetch(`${ttsBackendUrl}/tts/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(streamPayload),
  });
  return response.body as ReadableStream<Uint8Array>;
};

const requestTtsAudioAPI = async (payload: Payload): Promise<AudioSource> => {
  if (!isMediaSourceSupported()) {
    const streamBody = await requestTtsAudioStream(payload);

    const chunks = await readUint8ArrayStreamToChunks(streamBody);
    const blobUrl = createBlobUrl(chunks);
    return {
      type: 'url',
      playbackUrl: blobUrl,
      downloadUrl: blobUrl,
    };
  }

  return {
    type: 'stream',
    createStream: async () => {
      return requestTtsAudioStream(payload);
    }
  };
};

export const requestAudioAPI = async (
  payload: Payload,
): Promise<AudioSource> => {
  return requestTtsAudioAPI(payload);
};
